import {
    useQuery,
    useMutation,
    useQueryClient,
    UseQueryOptions,
    UseMutationOptions,
} from "@tanstack/react-query";
import { preferenceService } from "../PreferenceService";
import { Preference } from "../../models/Preference";

type MutationContext = {
    previous: Preference | undefined;
};

export interface UpdateUserSettingsResult {
    status: number;
    text: string;
}

// Hook to get user preferences
export function useUserPreferences(
    userId: number,
    options?: UseQueryOptions<Preference, Error, Preference>
) {
    return useQuery({
        queryKey: ["userPreferences", userId],
        queryFn: () => preferenceService.getUserSettings(userId),
        enabled: !!userId,
        ...options,
    });
}

// Hook to update user preferences
export function useUpdateUserPreferences(
    options?: UseMutationOptions<
        UpdateUserSettingsResult,
        Error,
        { userId: number; settings: Preference },
        MutationContext
    >
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ userId, settings }) => {
            return preferenceService.updateUserSettingsWithStatus(
                userId,
                settings
            );
        },
        onMutate: async ({ userId, settings }) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({
                queryKey: ["userPreferences", userId],
            });

            // Snapshot the previous value
            const previous = queryClient.getQueryData<Preference>([
                "userPreferences",
                userId,
            ]);

            // Optimistically update to the new value
            queryClient.setQueryData<Preference>(
                ["userPreferences", userId],
                settings
            );

            // Return a context object with the snapshotted value
            return { previous };
        },
        onError: (err, { userId }, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context?.previous) {
                queryClient.setQueryData(
                    ["userPreferences", userId],
                    context.previous
                );
            }
        },
        onSettled: (data, error, { userId }) => {
            // Always refetch after error or success
            queryClient.invalidateQueries({
                queryKey: ["userPreferences", userId],
            });
        },
        ...options,
    });
}
